import { SPELL_OUT_LIMIT, describeSeries } from './series';
import type { Formatter, Series } from './types';

/**
 * The categorical axis a set of series shares, and what a chart says about
 * itself to a screen reader.
 *
 * Pure and JSX-free, like `scale.ts` and `geometry.ts`, so it is testable in a
 * node environment.
 */

/** Every category key across the series, in first-seen order, each once. */
export function categoryKeys(seriesList: readonly Series[]): string[] {
  const seen = new Set<string>();
  const keys: string[] = [];
  for (const series of seriesList) {
    for (const point of series.points) {
      if (seen.has(point.key)) continue;
      seen.add(point.key);
      keys.push(point.key);
    }
  }
  return keys;
}

/**
 * Category order taken from the longest series, so one with a gap in it does
 * not shorten the axis for the rest.
 */
export function longestSeriesKeys(seriesList: readonly Series[]): string[] {
  const longest = seriesList.reduce<Series | undefined>(
    (best, series) => (!best || series.points.length > best.points.length ? series : best),
    undefined,
  );
  return longest?.points.map((point) => point.key) ?? [];
}

/** Key to display label, across every series. */
export function categoryLabels(seriesList: readonly Series[]): Map<string, string> {
  return new Map(
    seriesList.flatMap((series) => series.points.map((point) => [point.key, point.label] as const)),
  );
}

/** The longest label's length, never below one — what the axis thins its labels by. */
export function longestLabelLength(labels: ReadonlyMap<string, string>): number {
  return Math.max(...[...labels.values()].map((label) => label.length), 1);
}

interface ChartDescription {
  /** The svg's accessible name. */
  ariaLabel: string;
  /** True when the numbers are too many to speak and live in the hidden table instead. */
  usesTable: boolean;
}

/**
 * What the chart is called for assistive technology.
 *
 * Spelled out while it is short enough to listen to, summarised after that.
 * Past the limit the numbers live in the table, which can be navigated.
 */
export function describeChart(
  {
    title,
    summary,
    series,
  }: { title: string; summary?: string | undefined; series: readonly Series[] },
  format: Formatter,
): ChartDescription {
  const points = series[0]?.points.length ?? 0;
  const usesTable = series.length > 1 || points > SPELL_OUT_LIMIT;

  if (usesTable) return { ariaLabel: `${title}. ${summary ?? ''}`.trim(), usesTable };

  const readings = series.reduce(
    (sum, one) => sum + one.points.filter((point) => point.value !== null).length,
    0,
  );
  const ariaLabel =
    readings === 0
      ? `${title}: no data`
      : [title, ...series.map((one) => describeSeries(one, format))].join('. ');
  return { ariaLabel, usesTable };
}
